import React, { useState, useEffect } from 'react';
import { Grid, Input, Button } from 'semantic-ui-react';
import Select from 'react-select';
import axios from 'axios';
import { Fetchexecutequery, CreateData } from '../../../services/process/process_data/process_data';

const aggregateOptions = [
  { value: "SUM", label: "SUM" },
  { value: "COUNT", label: "COUNT" },
  { value: "AVG", label: "AVG" },
  { value: "MIN", label: "MIN" },
  { value: "MAX", label: "MAX" },
];

const logicOptions = [
  { value: "AND", label: "AND" },
  { value: "OR", label: "OR" }
];

const functionOptions = [
  { value: "IS NOT NULL", label: "IS NOT NULL" },
  { value: "IS NULL", label: "IS NULL" },
  { value: "EXISTS", label: "EXISTS" },
  { value: "NOT EXISTS", label: "NOT EXISTS" }
];

const QueryConfigurator = () => {
  const [databases, setDatabases] = useState([]);
  const [selectedDatabase, setSelectedDatabase] = useState(null);
  const [tables, setTables] = useState([]);
  const [headersByTable, setHeadersByTable] = useState({});

  const [queryName, setQueryName] = useState('');
  const [queryDescription, setQueryDescription] = useState('');
  const [columnGroups, setColumnGroups] = useState([]);
  const [tableList, setTableList] = useState([]);
  const [whereClauses, setWhereClauses] = useState([]);

  useEffect(() => {
    fetchDatabases();
  }, []);

  const fetchDatabases = async () => {
    const payload = { query: "SHOW DATABASES" };
    const creds = Fetchexecutequery();
    try {
      const res = await axios.post(creds[1], payload, creds[0]);
      const dbOptions = res.data.map(db => ({ value: db.Database, label: db.Database }));
      setDatabases(dbOptions);
    } catch (err) {
      alert("Failed to fetch databases");
    }
  };

  const fetchTables = async (dbName) => {
    const payload = { query: `SHOW TABLES FROM ${dbName}` };
    const creds = Fetchexecutequery();
    try {
      const res = await axios.post(creds[1], payload, creds[0]);
      const key = `Tables_in_${dbName}`;
      const options = res.data.map(t => ({ value: t[key], label: t[key] }));
      setTables(options);
    } catch (err) {
      alert("Failed to fetch tables");
    }
  };

  const fetchHeaders = async (db, table) => {
    const payload = { query: `DESC ${db}.${table}` };
    const creds = Fetchexecutequery();
    try {
      const res = await axios.post(creds[1], payload, creds[0]);
      const headerOptions = res.data.map(h => ({ value: h.Field, label: h.Field }));
      setHeadersByTable(prev => ({ ...prev, [table]: headerOptions }));
    } catch (err) {
      alert("Failed to fetch headers");
    }
  };

  const addColumnGroup = () => {
    setColumnGroups([...columnGroups, { table: null, columns: [], functions: {} }]);
  };

  const handleColumnGroupChange = (index, field, value) => {
    const updated = [...columnGroups];
    updated[index][field] = value;
    setColumnGroups(updated);
    if (field === 'table') fetchHeaders(selectedDatabase.value, value.value);
  };

  const handleFunctionChange = (groupIdx, column) => (val) => {
    const updated = [...columnGroups];
    updated[groupIdx].functions[column.value] = val;
    setColumnGroups(updated);
  };

  const addTable = () => {
    setTableList([...tableList, { table: null, alias: "" }]);
  };

  const handleTableChange = (index, field, value) => {
    const updated = [...tableList];
    updated[index][field] = value;
    setTableList(updated);
  };

  const addWhereClause = () => {
    setWhereClauses([...whereClauses, { table: null, column: null, function: null, logic: null }]);
  };

  const handleWhereChange = (index, field, value) => {
    const updated = [...whereClauses];
    updated[index][field] = value;
    setWhereClauses(updated);
    if (field === 'table') fetchHeaders(selectedDatabase.value, value.value);
  };

  const saveConfiguration = async () => {
    const payload = {
      query_name: queryName,
      description: queryDescription,
      database: selectedDatabase.value,
      columns: columnGroups,
      tables: tableList,
      where: whereClauses
    };
    const creds = CreateData();
    try {
      await axios.post(creds[1], payload, creds[0]);
      alert("Configuration saved successfully");
    } catch (err) {
      alert("Error saving configuration");
      console.error(err);
    }
  };

  return (
    <div style={{ padding: 20 }}>
      <h2>MySQL Query Configurator</h2>

      <Input
        placeholder="Query Name"
        value={queryName}
        onChange={(e) => setQueryName(e.target.value)}
        fluid
        style={{ marginBottom: 10 }}
      />
      <Input
        placeholder="Query Description"
        value={queryDescription}
        onChange={(e) => setQueryDescription(e.target.value)}
        fluid
        style={{ marginBottom: 20 }}
      />

      <Select
        options={databases}
        placeholder="Select Database"
        onChange={(val) => {
          setSelectedDatabase(val);
          fetchTables(val.value);
        }}
        value={selectedDatabase}
      />

      {/* Section 1: Columns */}
      <h3 style={{ marginTop: 30 }}>1. Columns to Fetch / Update</h3>
      {columnGroups.map((group, idx) => (
        <Grid key={idx}>
          <Grid.Row columns={3}>
            <Grid.Column>
              <Select
                options={tables}
                placeholder="Table"
                value={group.table}
                onChange={(val) => handleColumnGroupChange(idx, 'table', val)}
              />
            </Grid.Column>
            <Grid.Column>
              <Select
                isMulti
                placeholder="Columns"
                options={group.table ? headersByTable[group.table.value] || [] : []}
                value={group.columns}
                onChange={(val) => handleColumnGroupChange(idx, 'columns', val)}
              />
            </Grid.Column>
          </Grid.Row>
          {group.columns.map(col => (
            <Grid.Row key={col.value} columns={2}>
              <Grid.Column width={4}><b>{col.label}</b></Grid.Column>
              <Grid.Column width={6}>
                <Select
                  options={aggregateOptions}
                  value={group.functions[col.value] || null}
                  onChange={handleFunctionChange(idx, col)}
                  placeholder="Optional Function"
                />
              </Grid.Column>
            </Grid.Row>
          ))}
        </Grid>
      ))}
      <Button onClick={addColumnGroup}>Add Column Group</Button>

      {/* Section 2: Tables */}
      <h3 style={{ marginTop: 30 }}>2. Tables Involved</h3>
      {tableList.map((item, idx) => (
        <Grid key={idx}>
          <Grid.Row columns={2}>
            <Grid.Column>
              <Select
                options={tables}
                placeholder="Table"
                value={item.table}
                onChange={(val) => handleTableChange(idx, 'table', val)}
              />
            </Grid.Column>
            <Grid.Column>
              <Input
                placeholder="Alias (optional)"
                value={item.alias.value || ''}
                onChange={(e) => handleTableChange(idx, 'alias', { value: e.target.value })}
              />
            </Grid.Column>
          </Grid.Row>
        </Grid>
      ))}
      <Button onClick={addTable}>Add Table</Button>

      {/* Section 3: WHERE Clause */}
      <h3 style={{ marginTop: 30 }}>3. WHERE Clause Configuration</h3>
      {whereClauses.map((item, idx) => (
        <Grid key={idx}>
          <Grid.Row columns={4}>
            <Grid.Column>
              <Select
                options={tables}
                placeholder="Table"
                value={item.table}
                onChange={(val) => handleWhereChange(idx, 'table', val)}
              />
            </Grid.Column>
            <Grid.Column>
              <Select
                options={item.table ? headersByTable[item.table.value] || [] : []}
                placeholder="Column"
                value={item.column}
                onChange={(val) => handleWhereChange(idx, 'column', val)}
              />
            </Grid.Column>
            <Grid.Column>
              <Select
                options={functionOptions}
                placeholder="Function"
                value={item.function}
                onChange={(val) => handleWhereChange(idx, 'function', val)}
              />
            </Grid.Column>
            <Grid.Column>
              <Select
                options={logicOptions}
                placeholder="Logic (AND/OR)"
                value={item.logic}
                onChange={(val) => handleWhereChange(idx, 'logic', val)}
              />
            </Grid.Column>
          </Grid.Row>
        </Grid>
      ))}
      <Button onClick={addWhereClause}>Add Condition</Button>

      {/* Save */}
      <Button color="green" onClick={saveConfiguration} style={{ marginTop: 30 }}>Save Configuration</Button>
    </div>
  );
};

export default QueryConfigurator;
